import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Users, Sprout, AlertTriangle } from "lucide-react";
import { Input } from "../components/ui/input";
import { api, errMsg } from "../lib/api";
import { PageHeader } from "../components/app/PageHeader";
import { StatCard } from "../components/app/StatCard";

const iso = (d) => d.toLocaleDateString("en-CA");
const Level = ({ n, title, q, icon: Icon, children, testId }) => (
  <section className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid={testId}>
    <p className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-800/80"><Icon className="h-4 w-4" />Level {n}</p>
    <h2 className="mt-1 font-serif-display text-2xl font-semibold text-amber-950">{title}</h2>
    <p className="mb-4 text-sm text-stone-500">{q}</p>{children}
  </section>
);

const Feedback = ({ fb }) => fb.suppressed || !fb.responses ? (
  <p className="text-sm text-stone-500" data-testid="eval-feedback">{fb.responses ? `${fb.responses} responses — averages hidden until 5 or more.` : "No participant feedback in this period."}</p>
) : (
  <p className="text-sm text-stone-700" data-testid="eval-feedback">{fb.responses} voluntary responses · usefulness <b>{fb.avg_usefulness}</b>/5 · accessibility <b>{fb.avg_accessibility}</b>/5 · {fb.needs_more_help_yes} asked for further help</p>
);

const Unmet = ({ u }) => (
  <section className="rounded-xl border border-amber-500/30 bg-amber-50/50 p-5 sm:p-6" data-testid="unmet-needs">
    <h2 className="flex items-center gap-2 font-serif-display text-2xl font-semibold text-amber-950"><AlertTriangle className="h-5 w-5" />Unmet needs</h2>
    <ul className="mt-3 space-y-2 text-sm">
      {u.findings_without_response.map((f) => <li key={f.rule_key}><b>{f.title}</b> — <span className="text-stone-600">{f.evidence}</span></li>)}
      {u.approved_priorities_without_plan.map((p) => <li key={p.priority_id}>Approved priority without an action plan: <b>{p.title}</b></li>)}
      <li className="text-stone-600">Participants asking for further help: {u.participants_requesting_more_help} (monitoring) · {u.feedback_needing_more_help} (feedback)</li>
    </ul>
  </section>
);

const Reflections = ({ items }) => (
  <section className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid="reflections">
    <h2 className="font-serif-display text-2xl font-semibold text-amber-950">Reflections, challenges & lessons</h2>
    <div className="mt-3 space-y-4">
      {items.map((r) => (
        <Link key={r.plan_id} to={`/plans/${r.plan_id}`} className="block border-t border-amber-700/10 pt-3 text-sm hover:text-amber-900">
          <p className="font-semibold text-stone-800">{r.intervention}</p>
          {[["Findings", r.evaluation_findings], ["Challenges", r.challenges], ["Lessons", r.lessons_learned], ["Improvements", r.recommended_improvements]].filter(([, v]) => v).map(([k, v]) => <p key={k} className="text-stone-600"><b>{k}:</b> {v}</p>)}
        </Link>
      ))}
      {!items.length && <p className="text-sm text-stone-500">Reflections are written on each action plan during evaluation.</p>}
    </div>
  </section>
);

export default function Evaluation() {
  const now = new Date();
  const [r, setR] = useState({ start: iso(new Date(now.getTime() - 90 * 864e5)), end: iso(now) });
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/evaluation", { params: r }).then((x) => { setD(x.data); setErr(""); }).catch((e) => { setErr(errMsg(e)); setD(null); }); }, [r]);
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Mission evaluation" title="Mission Accomplishments" testId="evaluation-header">
        Three levels: what we did, whom we meaningfully reached, and what evidence shows the need was addressed. Attendance alone is never treated as success.
      </PageHeader>
      <div className="flex flex-wrap items-end gap-3" data-testid="eval-range">
        <label className="space-y-1"><span className="text-xs text-stone-600">From</span><Input type="date" value={r.start} onChange={(e) => setR({ ...r, start: e.target.value })} data-testid="eval-start-input" /></label>
        <label className="space-y-1"><span className="text-xs text-stone-600">To</span><Input type="date" value={r.end} onChange={(e) => setR({ ...r, end: e.target.value })} data-testid="eval-end-input" /></label>
      </div>
      {err && <p className="text-sm text-red-700" data-testid="eval-error">{err}</p>}
      {d && (
        <>
          <div className="grid gap-6 lg:grid-cols-3">
            <Level n={1} title="Pastoral activities" q="What activities and programs were carried out?" icon={Activity} testId="level-1">
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Activities recorded" value={d.level1.activities_recorded} testId="l1-activities" />
                <StatCard label="Sessions" value={d.level1.sessions_conducted} testId="l1-sessions" />
                <StatCard label="Completed" value={d.level1.programs_completed} testId="l1-completed" />
                <StatCard label="Evaluated" value={d.level1.programs_evaluated} testId="l1-evaluated" />
              </div>
            </Level>
            <Level n={2} title="Pastoral reach" q="How many people and households were meaningfully reached?" icon={Users} testId="level-2">
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Participants served" value={d.level2.participants_served} testId="l2-participants" />
                <StatCard label="Households reached" value={d.level2.households_reached} testId="l2-households" tip="Households reached by two programs are counted twice." />
                <StatCard label="Referrals issued" value={d.level2.referrals_issued} testId="l2-referrals" note={`${d.level2.referrals_completed} confirmed completed`} />
                <StatCard label="Asked for more help" value={d.level2.requested_more_help} testId="l2-more-help" />
              </div>
            </Level>
            <Level n={3} title="Pastoral outcomes" q="What evidence suggests the need was addressed?" icon={Sprout} testId="level-3">
              <ul className="space-y-2 text-sm">{d.level3.outcomes.map((o) => <li key={o.key} className="flex justify-between"><span>{o.label}</span><b className="font-mono-data">{o.count}</b></li>)}</ul>
              {d.level3.evaluated_without_rating > 0 && <p className="mt-2 text-xs text-amber-800">{d.level3.evaluated_without_rating} evaluated program(s) without an outcome judgment.</p>}
              <p className="mt-3 text-sm text-stone-700">Referral completion (confirmed): {d.level3.referral_completion_pct !== null ? `${d.level3.referral_completion_pct}%` : "no confirmations yet"}</p>
              <div className="mt-3"><Feedback fb={d.level3.participant_feedback} /></div>
            </Level>
          </div>
          <section className="overflow-x-auto rounded-xl border border-amber-700/15 bg-white p-5" data-testid="eval-by-area">
            <table className="w-full text-sm"><thead><tr className="text-left text-stone-500"><th className="py-2">Pastoral area</th><th>Activities</th><th>Participants</th><th>Households</th><th>Completed programs</th></tr></thead>
              <tbody>{d.by_area.map((a) => <tr key={a.area} className="border-t border-amber-700/10"><td className="py-2">{a.label}</td><td className="font-mono-data">{a.activities}</td><td className="font-mono-data">{a.participants_served}</td><td className="font-mono-data">{a.households_reached}</td><td className="font-mono-data">{a.programs_completed}</td></tr>)}</tbody></table>
          </section>
          <div className="grid gap-6 lg:grid-cols-2"><Unmet u={d.unmet} /><Reflections items={d.reflections} /></div>
          <p className="text-xs italic text-stone-500">{d.note}</p>
        </>
      )}
    </div>
  );
}
